// AGC: Add Stock Modal Component
// AGC: Allows manual entry of stock when supplies arrive from suppliers
// AGC: Includes optional price tracking for cost analysis

import React, { useState, useEffect, useMemo } from 'react'
import { X, Package, Plus, Minus, Check, Filter, ChevronDown, DollarSign, ToggleLeft, ToggleRight } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { motion, AnimatePresence } from 'framer-motion'
import type { BOMItem } from '../../services/bomService'
import type { InputCategory } from '../../types/inventory'
import { clampDecimals } from '../../utils'
import toast from 'react-hot-toast'

export interface StockEntryItem {
  inputId: string
  inputName: string
  category: InputCategory  // AGC: Added for auto-creating inventory items
  unit: string
  currentStock: number
  quantityToAdd: number
  fromLots: string[]
  // AGC: Price tracking fields (optional)
  pricePerUnit?: number       // AGC: Price per unit (R$/L, R$/kg)
  totalPrice?: number         // AGC: Total price for the quantity
  priceType?: 'perUnit' | 'total'  // AGC: Which price type user entered
}

export interface AddStockModalProps {
  isOpen: boolean
  onClose: () => void
  currentLotId: string
  currentLotName: string
  bomItems: BOMItem[]
  allLots?: Array<{ id: string; name: string; cropName: string }>
  onConfirmEntry: (entries: StockEntryItem[]) => Promise<void>
}

export const AddStockModal: React.FC<AddStockModalProps> = ({
  isOpen,
  onClose,
  currentLotId,
  currentLotName,
  bomItems,
  allLots = [],
  onConfirmEntry,
}) => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<StockEntryItem[]>([])
  const [selectedLots, setSelectedLots] = useState<string[]>([currentLotId])
  const [isLotsDropdownOpen, setIsLotsDropdownOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPrices, setShowPrices] = useState(false)  // AGC: Toggle price fields visibility

  // AGC: Initialize entries from BOM items
  useEffect(() => {
    if (isOpen && bomItems.length > 0) {
      const initialEntries: StockEntryItem[] = bomItems.map(item => ({
        inputId: item.inputId,
        inputName: item.inputName,
        category: item.category,  // AGC: Include category for auto-creating inventory items
        unit: item.unit,
        currentStock: item.currentStock,
        quantityToAdd: 0,
        fromLots: [currentLotId],
        pricePerUnit: undefined,
        totalPrice: undefined,
        priceType: 'perUnit',
      }))
      setEntries(initialEntries)
    }
  }, [isOpen, bomItems, currentLotId])

  // AGC: Filter entries based on selected lots
  const filteredEntries = useMemo(() => {
    // AGC: For now, show all entries since they're from the current lot
    // AGC: In future, filter by selectedLots
    return entries
  }, [entries, selectedLots])

  // AGC: Update quantity for an entry
  const updateQuantity = (inputId: string, delta: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.inputId === inputId) {
        const newQty = Math.max(0, entry.quantityToAdd + delta)
        return { ...entry, quantityToAdd: newQty }
      }
      return entry
    }))
  }

  // AGC: Set exact quantity and recalculate prices
  const setQuantity = (inputId: string, value: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.inputId === inputId) {
        const newQty = Math.max(0, value)
        // AGC: Recalculate total price if price per unit is set
        if (entry.pricePerUnit && entry.priceType === 'perUnit') {
          return { 
            ...entry, 
            quantityToAdd: newQty,
            totalPrice: newQty * entry.pricePerUnit 
          }
        }
        // AGC: Recalculate price per unit if total price is set
        if (entry.totalPrice && entry.priceType === 'total' && newQty > 0) {
          return { 
            ...entry, 
            quantityToAdd: newQty,
            pricePerUnit: entry.totalPrice / newQty 
          }
        }
        return { ...entry, quantityToAdd: newQty }
      }
      return entry
    }))
  }

  // AGC: Set price per unit
  const setPricePerUnit = (inputId: string, value: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.inputId === inputId) {
        const pricePerUnit = Math.max(0, value) || undefined
        // AGC: Calculate total price based on quantity
        const totalPrice = pricePerUnit && entry.quantityToAdd > 0 
          ? pricePerUnit * entry.quantityToAdd 
          : undefined
        return { 
          ...entry, 
          pricePerUnit,
          totalPrice,
          priceType: 'perUnit' as const
        }
      }
      return entry
    }))
  }

  // AGC: Set total price
  const setTotalPrice = (inputId: string, value: number) => {
    setEntries(prev => prev.map(entry => {
      if (entry.inputId === inputId) {
        const totalPrice = Math.max(0, value) || undefined
        // AGC: Calculate price per unit based on quantity
        const pricePerUnit = totalPrice && entry.quantityToAdd > 0 
          ? totalPrice / entry.quantityToAdd 
          : undefined
        return { 
          ...entry, 
          totalPrice,
          pricePerUnit,
          priceType: 'total' as const
        }
      }
      return entry
    }))
  }

  // AGC: Toggle price type for an entry
  const togglePriceType = (inputId: string) => {
    setEntries(prev => prev.map(entry => {
      if (entry.inputId === inputId) {
        return { 
          ...entry, 
          priceType: entry.priceType === 'perUnit' ? 'total' : 'perUnit'
        }
      }
      return entry
    }))
  }

  // AGC: Toggle lot selection
  const toggleLot = (lotId: string) => {
    setSelectedLots(prev => {
      if (prev.includes(lotId)) {
        // AGC: Don't remove if it's the only selected lot
        if (prev.length === 1) return prev
        return prev.filter(id => id !== lotId)
      }
      return [...prev, lotId]
    })
  }

  // AGC: Handle confirm
  const handleConfirm = async () => {
    const entriesToSubmit = entries.filter(e => e.quantityToAdd > 0)
    
    if (entriesToSubmit.length === 0) {
      toast.error('Adicione pelo menos um item')
      return
    }

    setIsSubmitting(true)
    try {
      await onConfirmEntry(entriesToSubmit)
      toast.success(t('lotPage.entrySuccess'))
      onClose()
    } catch (error) {
      console.error('AGC: Error adding stock:', error)
      toast.error(t('lotPage.entryError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Count items with quantity
  const itemsWithQuantity = entries.filter(e => e.quantityToAdd > 0).length

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white dark:bg-slate-800 w-full sm:w-[500px] sm:max-h-[85vh] max-h-[90vh] rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* AGC: Header */}
          <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-gray-700 px-4 py-4 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 dark:text-white">
                    {t('lotPage.stockEntryTitle')}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('lotPage.stockEntryDescription')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* AGC: Price Toggle */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => setShowPrices(!showPrices)}
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <DollarSign className="w-4 h-4" />
                <span>{t('prices.addPrices')}</span>
                {showPrices ? (
                  <ToggleRight className="w-5 h-5 text-green-500" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                )}
              </button>
              <span className="text-xs text-gray-400">
                {t('prices.optional')}
              </span>
            </div>

            {/* AGC: Lot Filter */}
            <div className="mt-3 relative">
              <button
                onClick={() => setIsLotsDropdownOpen(!isLotsDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-700 rounded-xl text-sm"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('lotPage.filterByLot')}: <strong>{currentLotName}</strong>
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isLotsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* AGC: Lots Dropdown */}
              {isLotsDropdownOpen && allLots.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 py-2 z-20 max-h-48 overflow-y-auto">
                  <p className="px-4 py-1 text-xs text-gray-400 uppercase tracking-wider">
                    {t('lotPage.currentLot')}
                  </p>
                  <button
                    onClick={() => toggleLot(currentLotId)}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-600"
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedLots.includes(currentLotId)
                        ? 'bg-green-500 border-green-500'
                        : 'border-gray-300 dark:border-gray-500'
                    }`}>
                      {selectedLots.includes(currentLotId) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-gray-900 dark:text-white">{currentLotName}</span>
                  </button>

                  {allLots.filter(l => l.id !== currentLotId).length > 0 && (
                    <>
                      <p className="px-4 py-1 text-xs text-gray-400 uppercase tracking-wider mt-2">
                        {t('lotPage.otherLots')}
                      </p>
                      {allLots.filter(l => l.id !== currentLotId).map(lot => (
                        <button
                          key={lot.id}
                          onClick={() => toggleLot(lot.id)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-600"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedLots.includes(lot.id)
                              ? 'bg-green-500 border-green-500'
                              : 'border-gray-300 dark:border-gray-500'
                          }`}>
                            {selectedLots.includes(lot.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <span className="text-sm text-gray-900 dark:text-white">{lot.name}</span>
                            <span className="text-xs text-gray-500 ml-2">{lot.cropName}</span>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* AGC: Items List */}
          <div className="overflow-y-auto max-h-[50vh] px-4 py-2">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('admin.noInputsFound')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEntries.map((entry) => (
                  <div
                    key={entry.inputId}
                    className={`p-3 rounded-xl border transition-colors ${
                      entry.quantityToAdd > 0
                        ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                        : 'border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-slate-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {entry.inputName}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t('lotPage.currentStock')}: {entry.currentStock.toFixed(1)} {entry.unit}
                        </p>
                      </div>
                      
                      {/* AGC: Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(entry.inputId, -1)}
                          disabled={entry.quantityToAdd === 0}
                          className="w-8 h-8 rounded-lg bg-white dark:bg-slate-600 border border-gray-200 dark:border-gray-600 flex items-center justify-center disabled:opacity-50"
                        >
                          <Minus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        
                        <input
                          type="number"
                          value={entry.quantityToAdd || ''}
                          onChange={(e) => setQuantity(entry.inputId, clampDecimals(parseFloat(e.target.value) || 0))}
                          placeholder="0"
                          className="w-16 h-8 text-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-slate-600 text-gray-900 dark:text-white text-sm font-medium"
                        />
                        
                        <button
                          onClick={() => updateQuantity(entry.inputId, 1)}
                          className="w-8 h-8 rounded-lg bg-white dark:bg-slate-600 border border-gray-200 dark:border-gray-600 flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        
                        <span className="text-xs text-gray-500 w-8">{entry.unit}</span>
                      </div>
                    </div>
                    
                    {/* AGC: Price Fields (shown when toggle is on) */}
                    {showPrices && entry.quantityToAdd > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2">
                          {/* AGC: Price Type Toggle */}
                          <button
                            onClick={() => togglePriceType(entry.inputId)}
                            className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                          >
                            {entry.priceType === 'perUnit' ? t('prices.perUnit') : t('prices.total')}
                          </button>
                          
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-gray-400 text-sm">R$</span>
                            {entry.priceType === 'perUnit' ? (
                              <input
                                type="number"
                                value={entry.pricePerUnit || ''}
                                onChange={(e) => setPricePerUnit(entry.inputId, clampDecimals(parseFloat(e.target.value) || 0))}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                className="flex-1 h-7 px-2 text-center rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-slate-600 text-gray-900 dark:text-white text-sm"
                              />
                            ) : (
                              <input
                                type="number"
                                value={entry.totalPrice || ''}
                                onChange={(e) => setTotalPrice(entry.inputId, clampDecimals(parseFloat(e.target.value) || 0))}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                className="flex-1 h-7 px-2 text-center rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-slate-600 text-gray-900 dark:text-white text-sm"
                              />
                            )}
                            <span className="text-xs text-gray-400">
                              {entry.priceType === 'perUnit' ? `/${entry.unit}` : t('prices.totalLabel')}
                            </span>
                          </div>
                        </div>
                        
                        {/* AGC: Show calculated value */}
                        {entry.pricePerUnit && entry.totalPrice && (
                          <p className="text-xs text-gray-500 mt-1 text-right">
                            {entry.priceType === 'perUnit' 
                              ? `${t('prices.total')}: R$ ${entry.totalPrice.toFixed(2)}`
                              : `${t('prices.perUnit')}: R$ ${entry.pricePerUnit.toFixed(2)}/${entry.unit}`
                            }
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AGC: Footer */}
          <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-gray-700 px-4 py-4 safe-area-inset-bottom">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">
                {itemsWithQuantity} {itemsWithQuantity === 1 ? 'item' : 'itens'} selecionado{itemsWithQuantity !== 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={itemsWithQuantity === 0 || isSubmitting}
                className={`flex-1 py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                  itemsWithQuantity === 0 || isSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>{t('lotPage.confirmEntry')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default AddStockModal

